<template>
  <div class="border">
    <div class="d-flex gap-2 bg-gray-100 text-gray-600 line-height-10 p-1">
      <Button
        v-if="isEditMode"
        :disabled="isPickButtonDisabled"
        @click="pick"
        title="Pick from the approved message list"
      >
        <i class="fas fa-list fa-fw"></i>
      </Button>
      <Button
        v-if="isEditMode"
        :disabled="isEmpty"
        @click="clear"
        title="Clear"
      >
        <i class="fas fa-trash-alt fa-fw"></i>
      </Button>
      <Button
        v-if="isPickMode"
        :disabled="! hasCurrent"
        @click="confirm"
        title="Confirm"
      >
        <i class="fas fa-check fa-fw"></i>
      </Button>
      <Button
        v-if="isPickMode"
        @click="cancel"
        title="Cancel"
      >
        <i class="fas fa-times fa-fw"></i>
      </Button>
    </div>
    <div v-if="isEditMode">
      <TextInput
        :value="text"
        @input="onTextInput"
        type="textarea"
        :autosize="{minRows: 1, maxRows: 6}"
        resize="none"
        :readonly="isEditControlDisabled"
        placeholder="Write down a message here"
      >
        <template #attachments>
          <slot name="attachments"></slot>
        </template>
        <template #bottom-left-tools-before>
          <slot name="bottom-left-tools-before"></slot>
        </template>
        <template #bottom-left-tools-after>
          <slot name="bottom-left-tools-after"></slot>
        </template>
        <template #bottom-right-tools>
          <slot name="bottom-right-tools"></slot>
        </template>
      </TextInput>
    </div>
    <ApprovedMessagesList
      v-if="isPickMode"
      :url="approvedMessageListUrl"
      @select="onSelect"
      class="px-2 pt-2"
    ></ApprovedMessagesList>
  </div>
</template>

<script>
import ApprovedMessagesList from "./approved-messages-list";
import TextInput from "./text-input";
import Button from "./button";
import {isBlank} from "../../../helpers";

const modes = {
  edit: 'edit',
  pick: 'pick',
}

export default {
  components: {
    Button,
    TextInput,
    ApprovedMessagesList,
  },

  props: {
    value: Object,
    approvedMessageListUrl: String,
    canSendSms: Boolean,
    canSendApprovedSms: Boolean,
  },

  data() {
    return {
      current: null,
    }
  },

  computed: {
    /**
     * @return {string|null}
     */
    text() {
      return _.get(this.value, 'text', null);
    },

    /**
     * @return {boolean}
     */
    isEmpty() {
      return isBlank(this.text);
    },

    /**
     * @return {string}
     */
    mode() {
      return _.get(this.value, 'mode', modes.edit);
    },

    /**
     * @return {boolean}
     */
    isEditMode() {
      return this.mode === modes.edit;
    },

    /**
     * @return {boolean}
     */
    isPickMode() {
      return this.mode === modes.pick;
    },

    /**
     * @return {boolean}
     */
    hasCurrent() {
      return _.isObject(this.current);
    },

    /**
     * @return {boolean}
     */
    isEditControlDisabled() {
      return ! Boolean(this.canSendSms);
    },

    /**
     * @return {boolean}
     */
    isPickButtonDisabled() {
      return ! Boolean(this.canSendApprovedSms);
    },
  },

  methods: {
    /**
     * @emits input
     */
    pick() {
      this.emit({mode: modes.pick});
    },

    /**
     * @emits input
     */
    clear() {
      this.emit(null);
    },

    /**
     * @emits input
     */
    confirm() {
      this.emit(_.assign({}, this.current, {mode: modes.edit}));
      this.current = null;
    },

    /**
     * @emits input
     */
    cancel() {
      this.emit({mode: modes.edit});
      this.current = null;
    },

    /**
     * @param item
     */
    onSelect({model: {id, content}}) {
      this.current = {text: content, id};
    },

    /**
     * @param text
     * @emits input
     */
    onTextInput(text) {
      this.emit({text, id: null, mode: modes.edit});
    },

    /**
     * @param {Object|null} value
     * @emits input
     */
    emit(value) {
      const payload = _.isObject(value)
        ? _.defaults(value, this.value || {text: null, id: null})
        : null;

      this.$emit('input', payload);
    },
  },
}
</script>
